import { MenuGapsEnum } from "@pages/CategoryProfile/sections/Menu/Menu.constants";

/** Dot fill color keyed by which of the four plot quadrants the point falls in.
 *  Quadrants are relative to the geometric center (50 % / 50 %) of the plot area.
 *  See Figma node 430:17039 for color provenance.
 */
export const DOT_COLORS_BY_QUADRANT = {
    topLeft:     '#4FBBF8',
    topRight:    '#225E80',
    bottomLeft:  '#00E1AF',
    bottomRight: '#C730BE',
} as const;

/** Axis-line / border color (Figma: border/cell-light). */
export const COLOR_AXIS_LINE = '#465A77';

/** Neutral quadrant-divider dash color (Figma: ui-blu-gry/400). */
export const COLOR_DIVIDER_NEUTRAL = '#283547';

/** Highlighted-quadrant inner-edge divider color (Figma: text/link). */
export const COLOR_DIVIDER_HIGHLIGHT = '#00B48C';

/** Highlighted-quadrant background fill — semi-transparent. */
export const COLOR_HIGHLIGHT_FILL = 'rgba(40,53,71,0.4)';

/** Dot radius (diameter 10 px per spec). */
export const DOT_RADIUS = 5;

/** Width reserved for Y-axis labels (px). */
export const Y_AXIS_WIDTH = 72;

/** Width of the right-hand Innovation Opportunity column (px). Figma node 22:4250. */
export const INNOVATION_COLUMN_WIDTH = 72;

/** Vertical padding to keep dots from clipping at the SVG edge (px). */
export const PLOT_PADDING = DOT_RADIUS + 2;

/** Gap between a dot's edge and the custom tooltip box (px). */
export const TOOLTIP_GAP = 8;

/** Minimum distance the tooltip box must keep from the plot container's edges (px). */
export const TOOLTIP_EDGE_PAD = 4;

/** Size (px) of the triangular caret drawn on the tooltip box, pointing at the hovered dot. */
export const ARROW_SIZE = 6;

/** Minimum distance the caret must keep from either rounded corner of the tooltip box (px). */
export const ARROW_MARGIN = 10;

/**
 * Fraction of each axis's raw data range added as symmetric padding to both ends of the
 * plotted domain, so the extreme dot on each end doesn't pin to the plot border.
 * The resulting border inset is frac / (1 + 2*frac); 0.03191 -> the extreme dot sits ~3% in
 * from each border after PLOT_PADDING's own pixel margin is applied. Padding is symmetric
 * (same fraction added to both min and max) so the domain midpoint — and therefore quadrant
 * membership — is unchanged.
 */
export const DOMAIN_PAD_FRACTION = 0.03191;

/** The four quadrant keys — derived from DOT_COLORS_BY_QUADRANT to stay in sync. */
export type QuadrantKey = keyof typeof DOT_COLORS_BY_QUADRANT;

/** Per-view axis/metric configuration for the scatter plot. */
export interface ViewConfig {
    xKey:   'penetration' | 'fourYearGrowth';
    yKey:   'loveLikeIt' | 'knowIt';
    xLabel: string;
    yLabel: string;
    /**
     * Which quadrant(s) are highlighted (receive the fill + tinted divider color).
     * Popularity: top-left only.
     * Trend Gap: top-right + bottom-right.
     */
    highlightedQuadrants: QuadrantKey[];
    /** Green callout text shown above the chart for this view (both views have one), or null. */
    calloutText: string | null;
    /**
     * Whether the right-hand "Innovation Opportunity" risk scale is rendered.
     * Trend Gap only; Popularity reserves an empty column of the same width so both views match in size.
     */
    showInnovationScale: boolean;
}

export const VIEW_CONFIG: Record<MenuGapsEnum, ViewConfig> = {
    [MenuGapsEnum.POPULARITY]: {
        xKey:   'penetration',
        yKey:   'loveLikeIt',
        xLabel: 'Menu Penetration',
        // AC explicitly overrides Figma label "Consumer Affinity" with the metric name.
        yLabel: 'Love It / Like It',
        highlightedQuadrants: ['topLeft'],
        calloutText: 'Items or flavors that are well-liked by consumers but underrepresented on menus today could signal an area ripe for focus.',
        showInnovationScale: false,
    },
    [MenuGapsEnum.TREND_GAP]: {
        xKey:   'fourYearGrowth',
        yKey:   'knowIt',
        xLabel: '4-Year Menu Growth',
        yLabel: 'Know It',
        highlightedQuadrants: ['topRight', 'bottomRight'],
        calloutText: 'Items or flavors that are growing on menus but not yet familiar to consumers could signal early-stage trends worth watching.',
        showInnovationScale: true,
    },
};
